import { InventoryRecord } from '../types/inventory.types';
import {
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
} from '../constants/inventory-status-labels';
import { formatDateTime } from './formatters/date';
import { formatCurrency } from './formatters/money';
import { computeInventoryRecordPricingTotals } from './inventoryRecordTotals';
import {
  buildPrintChromeHtml,
  escapeHtml,
  openPrintWindow,
  PRINT_POPUP_BLOCKED_MESSAGE,
} from './printWindow';

export { escapeHtml } from './printWindow';

// Helper function to get status badge class
export const getStatusBadgeClass = (status: string) => {
  if (status?.toLowerCase() === 'completed' || status === 'Completado') {
    return 'status-completado';
  } else if (status?.toLowerCase() === 'cancelled' || status === 'Cancelado') {
    return 'status-cancelado';
  }
  return 'status-pendiente';
};

// Helper function to format status for display
export const formatStatus = (status: string) => {
  if (status === 'COMPLETED') return 'Completado';
  if (status === 'CANCELLED') return 'Cancelado';
  if (status === 'PENDING') return 'Pendiente';
  return status;
};

// Helper function to format warehouse product status
export const formatWarehouseStatus = (status: string) => {
  if (status === 'ASSIGNED') return INV_STATUS_BRANCH_LABEL;
  if (status === 'IN_TRANSIT') return 'En Tránsito';
  if (status === 'UNASSIGNED') return INV_STATUS_WAREHOUSE_LABEL;
  return status;
};

// Helper function to get warehouse status class
export const getWarehouseStatusClass = (status: string) => {
  if (status === 'ASSIGNED') return 'status-assigned';
  if (status === 'IN_TRANSIT') return 'status-in-transit';
  if (status === 'UNASSIGNED') return 'status-unassigned';
  return 'status-unassigned';
};

// Helper function to get warehouse products for a specific item
export const getWarehouseProductsForItem = (item: any, warehouseProducts?: any[]): any[] => {
  if (!warehouseProducts) return [];
  // Filter warehouse products that match this item's product
  return warehouseProducts.filter((wp) => wp.productCode === item.product?.code);
};

/**
 * Print handler for inventory record details
 * Generates a print-ready HTML document with all record information
 */
export const printInventoryRecord = (record: InventoryRecord) => {
  if (!record) return;

  // Build print HTML programmatically to ensure all details are included
  const productsHtml =
    record.items
      ?.map((item) => {
        const warehouseProducts = getWarehouseProductsForItem(item, record.warehouseProducts);
        const hasWarehouseProducts = warehouseProducts.length > 0;

        let warehouseProductsHtml = '';
        if (hasWarehouseProducts) {
          warehouseProductsHtml = `
        <div class="product-details">
          <div class="product-details-title">
            Detalles de Productos Individuales (${warehouseProducts.length})
          </div>
          <table>
            <thead>
              <tr>
                <th>Código Único</th>
                <th>Nombre del Producto</th>
                <th>Tipo de Producto</th>
                <th>Estado</th>
                <th>Material</th>
              </tr>
            </thead>
            <tbody>
              ${warehouseProducts
                .map(
                  (wp) => `
                <tr>
                  <td>${escapeHtml(wp.productCodeUnique) || '-'}</td>
                  <td>${escapeHtml(wp.productName) || '-'}</td>
                  <td>${escapeHtml(wp.productType) || '-'}</td>
                  <td>
                    <span class="${getWarehouseStatusClass(wp.status)}">
                      ${escapeHtml(formatWarehouseStatus(wp.status))}
                    </span>
                  </td>
                  <td>${wp.material?.name || wp.material?.id ? escapeHtml(wp.material?.name || wp.material?.id) : 'N/A'}</td>
                </tr>
              `,
                )
                .join('')}
            </tbody>
          </table>
        </div>
      `;
        }

        return `
      <div class="product-item">
        <div class="product-name">${escapeHtml(item.product?.name || `Producto ${item.productId}`)}</div>
        <div class="product-code">${escapeHtml(item.product?.code) || '-'}</div>
        <div style="margin-top: 8px;">
          <span class="product-quantity">${escapeHtml(item.quantity)} pza.</span>
        </div>
        ${warehouseProductsHtml}
      </div>
    `;
      })
      .join('') || '<div class="product-item">No hay líneas de producto en este registro</div>';

  const totalUnits = record.items?.reduce((sum, item) => sum + Number(item.quantity || 0), 0) || 0;
  const isCompleted = record.status === 'COMPLETED';
  const pricingTotals = isCompleted
    ? computeInventoryRecordPricingTotals(record.warehouseProducts)
    : null;

  const printHtml = `
    <html>
      <head>
        <title>Detalles del Registro de Inventario - ${escapeHtml(record.recordCode)}</title>
        <style>
          body { 
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
            margin: 0; 
            padding: 20px; 
            background: white; 
            color: #1f2937;
          }
          .header {
            background: linear-gradient(to right, #06b6d4, #10b981);
            color: white;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 20px;
          }
          .header h2 {
            margin: 0;
            font-size: 20px;
            font-weight: bold;
          }
          .info-card {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 12px;
            padding-bottom: 12px;
            border-bottom: 1px solid #f3f4f6;
          }
          .info-row:last-child {
            border-bottom: none;
            margin-bottom: 0;
            padding-bottom: 0;
          }
          .info-label {
            font-size: 12px;
            color: #6b7280;
            font-weight: 500;
            margin-bottom: 4px;
          }
          .info-value {
            font-size: 14px;
            color: #111827;
            font-weight: 600;
          }
          .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
          }
          .status-completado {
            background: #d1fae5;
            color: #065f46;
          }
          .status-cancelado {
            background: #fee2e2;
            color: #991b1b;
          }
          .status-pendiente {
            background: #dbeafe;
            color: #1e40af;
          }
          .products-section {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            overflow: hidden;
            margin-bottom: 20px;
          }
          .products-header {
            background: #f9fafb;
            padding: 12px 20px;
            border-bottom: 1px solid #e5e7eb;
            font-weight: 600;
            font-size: 14px;
            color: #374151;
          }
          .product-item {
            padding: 12px 20px;
            border-bottom: 1px solid #f3f4f6;
          }
          .product-item:last-child {
            border-bottom: none;
          }
          .product-name {
            font-weight: 600;
            font-size: 14px;
            color: #111827;
            margin-bottom: 4px;
          }
          .product-code {
            font-size: 12px;
            color: #6b7280;
            margin-bottom: 8px;
          }
          .product-quantity {
            display: inline-block;
            background: #f3f4f6;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
            color: #374151;
          }
          .product-details {
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid #e5e7eb;
            background: #f9fafb;
            padding: 12px;
            border-radius: 4px;
          }
          .product-details-title {
            font-size: 12px;
            font-weight: 600;
            color: #374151;
            margin-bottom: 8px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
            margin-top: 8px;
          }
          th, td {
            padding: 8px;
            text-align: left;
            border-bottom: 1px solid #e5e7eb;
          }
          th {
            background: #f9fafb;
            font-weight: 600;
            color: #374151;
            font-size: 10px;
            text-transform: uppercase;
          }
          tr:hover {
            background: #f9fafb;
          }
          .status-assigned {
            background: #d1fae5;
            color: #065f46;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 10px;
            font-weight: 600;
          }
          .status-in-transit {
            background: #dbeafe;
            color: #1e40af;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 10px;
            font-weight: 600;
          }
          .status-unassigned {
            background: #f3f4f6;
            color: #374151;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 10px;
            font-weight: 600;
          }
          .total-section {
            background: #f9fafb;
            padding: 16px 20px;
            border-top: 1px solid #e5e7eb;
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 12px;
          }
          .total-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 16px;
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
          }
          .total-label {
            font-size: 11px;
            font-weight: 600;
            color: #6b7280;
            text-transform: uppercase;
          }
          .total-value {
            font-size: 14px;
            font-weight: 700;
            color: #06b6d4;
          }
          .total-value-cost {
            color: #3b82f6;
          }
          .total-value-sale {
            color: #10b981;
          }
          .footer {
            text-align: center;
            padding: 20px;
            color: #6b7280;
            font-size: 11px;
            border-top: 1px solid #e5e7eb;
            margin-top: 20px;
          }
          @media print {
            body { 
              margin: 0; 
              padding: 20px; 
            }
            .no-print { 
              display: none; 
            }
            @page {
              margin: 0;
              size: auto;
            }
            /* Hide browser default headers and footers */
            @page :first {
              margin: 0;
            }
            @page :left {
              margin: 0;
            }
            @page :right {
              margin: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>Detalles del Registro de Inventario</h2>
        </div>

        <div class="info-card">
          <div class="info-row">
            <div>
              <div class="info-label">Número de Registro</div>
              <div class="info-value">${escapeHtml(record.recordCode)}</div>
            </div>
            <div>
              <span class="status-badge ${getStatusBadgeClass(record.status || '')}">
                ${escapeHtml(formatStatus(record.status || ''))}
              </span>
            </div>
          </div>
          <div class="info-row">
            <div>
              <div class="info-label">Ubicación</div>
              <div class="info-value">${record.location ? escapeHtml(record.location) : 'N/A'}</div>
            </div>
            <div>
              <div class="info-label">Fecha de creación</div>
              <div class="info-value">${record.createdAt ? escapeHtml(formatDateTime(record.createdAt) || 'N/A') : 'N/A'}</div>
            </div>
          </div>
          <div class="info-row">
            <div>
              <div class="info-label">Fecha de registro</div>
              <div class="info-value">${record.completedAt ? escapeHtml(formatDateTime(record.completedAt) || 'N/A') : 'N/A'}</div>
            </div>
          </div>
        </div>

        <div class="products-section">
          <div class="products-header">
            Productos (${record.items?.length || 0})
          </div>
          ${productsHtml}
          ${
            record.items && record.items.length > 0
              ? `
            <div class="total-section">
              <div class="total-item">
                <span class="total-label">Total de piezas</span>
                <span class="total-value">${totalUnits} piezas</span>
              </div>
              ${
                pricingTotals
                  ? `
              <div class="total-item">
                <span class="total-label">Costo total</span>
                <span class="total-value total-value-cost">${escapeHtml(formatCurrency(pricingTotals.totalCostPrice))}</span>
              </div>
              <div class="total-item">
                <span class="total-label">Precio de Venta Total</span>
                <span class="total-value total-value-sale">${escapeHtml(formatCurrency(pricingTotals.totalSalePrice))}</span>
              </div>
              `
                  : ''
              }
            </div>
          `
              : ''
          }
        </div>

        <div class="footer">
          <div>Generado el ${new Date().toLocaleString('es-ES')}</div>
          <div>Sistema de Gestión de Inventarios</div>
        </div>

        ${buildPrintChromeHtml()}
      </body>
    </html>
  `;

  const printWindow = openPrintWindow({
    html: printHtml,
    size: 'document',
    windowName: 'printInventoryRecord',
  });

  if (!printWindow) {
    window.alert(PRINT_POPUP_BLOCKED_MESSAGE);
  }
};
